import React, { useState, useMemo, useRef, useEffect } from 'react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { previewCatalogTool, processCatalogTool } from '../../../api/catalog_tool';
import ScopeSelector from './ScopeSelector';
import BulkPasteCard from './BulkPasteCard';
import BulkRunReport from './BulkRunReport';
import './BulkLinkTab.scss';

const MAX_LINKS = 1000;

const LINK_MODES = [
  { value: 'auto', title: 'Auto-detect (default)', description: 'Scraper sorts variants and pins' },
  { value: 'force_variant', title: 'Force as variant', description: 'For DTC source-of-truth URL sets from brand partners' }
];

/** Bulk Link: PID hard gate → paste URLs → auto-classified variant/pin linking with the variant→pin cascade. */
const BulkLinkTab = () => {
  const [selected, setSelected] = useState(null);
  const [links, setLinks] = useState('');
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [linkMode, setLinkMode] = useState('auto');
  const [processing, setProcessing] = useState(false);
  const [report, setReport] = useState(null);

  const lines = useMemo(() => links.split('\n').map(line => line.trim()).filter(Boolean), [links]);
  const overLimit = lines.length > MAX_LINKS;

  const latestPreviewId = useRef(0);
  const previewDebounced = useMemo(
    () =>
      AwesomeDebouncePromise(async body => {
        const previewId = ++latestPreviewId.current;
        const response = await previewCatalogTool(body);
        return { previewId, data: response.data };
      }, 600),
    []
  );

  useEffect(() => {
    if (!selected || !lines.length || overLimit) {
      latestPreviewId.current++;
      setPreview(null);
      setIsPreviewing(false);
      return;
    }
    setIsPreviewing(true);
    previewDebounced({ links: lines, mode: 'link', Product_id: selected.id, linkMode })
      .then(({ previewId, data }) => {
        if (previewId !== latestPreviewId.current) return;
        setPreview(data);
        setIsPreviewing(false);
      })
      .catch(error => {
        setPreview(null);
        setIsPreviewing(false);
        window.ALERT.error(error || 'Failed to classify links');
      });
  }, [lines, selected, overLimit, linkMode, previewDebounced]);

  const okCount = preview?.summary?.byStatus?.ok || 0;
  const invalidCount = preview?.summary?.invalid || 0;

  const disabledReason = !selected
    ? 'Confirm a product ID to begin'
    : !lines.length
    ? 'Paste at least one URL'
    : overLimit
    ? `Over the ${MAX_LINKS.toLocaleString()}-link limit`
    : isPreviewing
    ? 'Classifying links…'
    : invalidCount > 0
    ? 'Fix or remove invalid lines first'
    : okCount === 0
    ? 'Nothing linkable in this batch'
    : null;

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const response = await processCatalogTool({ links: lines, mode: 'link', Product_id: selected.id, linkMode });
      setReport(response.data);
      setLinks('');
      setPreview(null);
      window.ALERT.success(`Linked ${response.data.linkedAsVariant + response.data.linkedAsPin} URLs`);
    } catch (error) {
      window.ALERT.error(error || 'Failed to link URLs');
    } finally {
      setProcessing(false);
    }
  };

  const clearScope = () => {
    setSelected(null);
    setPreview(null);
    setReport(null);
  };

  return (
    <div className='catalog-link-tab'>
      <div className='tab-columns'>
        <div className='left-column'>
          <section className='scope-section'>
            <h2>1 · Product ID (required)</h2>
            <ScopeSelector
              mode='link'
              scopeType='product'
              onScopeTypeChange={() => {}}
              selected={selected}
              onSelect={setSelected}
              onClear={clearScope}
            />
          </section>

          <BulkPasteCard
            mode='link'
            heading='2 · Paste URLs to link'
            links={links}
            onChange={setLinks}
            disabled={!selected}
            disabledHelper='Enter a product ID to begin. Products must exist before linking.'
            previewRows={preview?.rows}
            summary={preview?.summary}
            isPreviewing={isPreviewing}
            maxLinks={MAX_LINKS}
          />
        </div>

        <div className='right-column'>
          <h2>3 · Link mode</h2>

          <div className='option-group'>
            {LINK_MODES.map(option => (
              <label key={option.value} className='option'>
                <input
                  type='radio'
                  name='ct-linkMode'
                  value={option.value}
                  checked={linkMode === option.value}
                  onChange={e => setLinkMode(e.target.value)}
                  disabled={!selected}
                />
                <div className='option-content'>
                  <span className='option-title'>{option.title}</span>
                  <span className='option-description'>{option.description}</span>
                </div>
              </label>
            ))}
          </div>

          <button className='submit-button' onClick={handleProcess} disabled={processing || !!disabledReason}>
            {processing ? 'Linking…' : okCount > 0 ? `Bulk link ${okCount.toLocaleString()} URLs` : 'Bulk link URLs'}
          </button>
          {!processing && disabledReason && <div className='cta-reason'>{disabledReason}</div>}
          <div className='cta-note'>Variants auto-link matching same-URL pins — no prompt, summary report after.</div>
        </div>
      </div>

      {report && <BulkRunReport mode='link' report={report} />}
    </div>
  );
};

export default BulkLinkTab;
